import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Textarea } from '../components/Textarea';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

export const ManuscriptPreviewPage = () => {
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [saved, setSaved] = useState(false);
  const [manuscript, setManuscript] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Extract manuscript ID from URL - handles UUIDs
  const manuscriptId = window.location.pathname.match(/\/manuscripts\/([a-zA-Z0-9-]+)\/preview/)?.[1];

  useEffect(() => {
    if (manuscriptId) {
      fetchManuscript();
    } else {
      setError('Invalid manuscript ID');
      setLoading(false);
    }
  }, [manuscriptId]);

  const fetchManuscript = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.manuscripts.detail(manuscriptId!), {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to load manuscript');
        setLoading(false);
        return;
      }

      // Transform backend data to frontend format
      const m = data.manuscript;
      const transformedManuscript = {
        id: m.id,
        title: m.title,
        author: {
          name: m.author?.displayName || 'Unknown Author',
          bio: m.author?.bio || '',
        },
        genre: formatGenre(m.primaryGenre),
        subGenres: m.secondaryGenres ? m.secondaryGenres.map(formatGenre) : [],
        wordCount: m.wordCount,
        completionStatus: m.status === 'COMPLETE' ? 'Complete' : 'In Progress',
        synopsis: m.synopsis,
        contentWarnings: m.contentWarnings
          ? (Array.isArray(m.contentWarnings) ? m.contentWarnings : m.contentWarnings.split(',').map((w: string) => w.trim()).filter(Boolean))
          : [],
        targetAudience: m.targetAudience || 'Adult',
        firstPages: m.firstPages || '',
        seekingBetaReaders: m.seekingBetaReaders,
        betaDeadline: m.betaDeadline,
        // Fields not in database schema - using defaults
        lookingFor: m.lookingFor || [
          'Overall impression and emotional impact',
          'Pacing and plot structure',
          'Character development',
          'Grammar and prose quality',
        ],
        timeline: m.timeline || {
          readingDeadline: m.betaDeadline ? new Date(m.betaDeadline).toISOString().split('T')[0] : 'TBD',
          estimatedWeeks: 'To be discussed',
          feedbackFormat: 'Overall summary',
        },
        writerPreferences: m.writerPreferences || {
          communicationStyle: 'Open to discussion',
          feedbackStyle: 'Honest and constructive',
          experienceLevel: 'Any level welcome',
        },
      };

      setManuscript(transformedManuscript);
      setLoading(false);
    } catch (err) {
      console.error('Fetch manuscript error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  // Helper to format backend genre enum to frontend
  const formatGenre = (genre: string) => {
    const genreMap: Record<string, string> = {
      FANTASY: 'Fantasy',
      SCIENCE_FICTION: 'Science Fiction',
      MYSTERY: 'Mystery',
      THRILLER: 'Thriller',
      ROMANCE: 'Romance',
      LITERARY_FICTION: 'Literary Fiction',
      HISTORICAL_FICTION: 'Historical Fiction',
      HORROR: 'Horror',
      YOUNG_ADULT: 'Young Adult',
      MIDDLE_GRADE: 'Middle Grade',
      CONTEMPORARY: 'Contemporary',
      PARANORMAL: 'Paranormal',
      DYSTOPIAN: 'Dystopian',
      OTHER: 'Other',
    };
    return genreMap[genre] || genre;
  };

  // Unused mock data - keeping for reference
  const _unused_mockManuscript = {
    id: manuscriptId,
    title: 'Whispers in the Dark',
    author: {
      name: 'Rachel Green',
      bio: 'Rachel Green is a horror writer based in Portland, Oregon. She has published short stories in various literary magazines and is currently seeking beta readers for her debut novel.',
    },
    genre: 'Horror',
    subGenres: ['Psychological Horror', 'Supernatural'],
    wordCount: 72000,
    completionStatus: 'Complete',
    synopsis: `In the quiet town of Millbrook, nothing ever happens—or so everyone believes. But when Emma Crawford moves into the old Victorian house on Maple Street, she quickly discovers that the town harbors a terrifying secret that awakens every full moon.

Strange sounds echo through her house at night. Shadows move where they shouldn't. And the townspeople refuse to speak about what happened to the previous residents.

As Emma investigates, she uncovers a dark history stretching back generations: a curse that binds the town to an ancient evil, demanding a terrible price. Now, with the next full moon approaching, Emma must decide whether to flee—or stay and fight against something far more powerful than she ever imagined.

But the deeper she digs, the more she realizes that leaving might not be an option. The house has chosen her, and the curse has already begun to take hold.`,
    contentWarnings: ['Violence', 'Gore', 'Death', 'Body Horror'],
    targetAudience: 'Adult',
    firstPages: `CHAPTER ONE

The house looked different in the daylight. Less menacing, somehow. Emma Crawford stood on the cracked sidewalk, keys clutched in her hand, and tried to reconcile the charming Victorian before her with the nightmare-inducing structure she'd seen in the photographs.

"You're sure about this?" Her sister's voice crackled through the phone speaker. "It's not too late to back out."

"I'm sure," Emma lied. The truth was, she wasn't sure about anything anymore. Not since David left. Not since she'd lost the job she'd spent five years building. Not since her entire life had imploded in the span of three months.

But the house was hers now. Bought for a fraction of what it should have cost, courtesy of a motivated seller who'd seemed almost desperate to be rid of it. Emma had chalked it up to the real estate market, the town's declining population, the general state of disrepair.

She'd been wrong about a lot of things lately.

"Emma? You still there?"

"Yeah, sorry. I should go. The movers will be here soon."

"Call me tonight, okay? I want to make sure you're settling in."

"I will. Love you."

Emma ended the call and climbed the steps to the front porch. The wood creaked under her weight, and somewhere in the distance, a crow cawed. She inserted the key into the lock, noting how easily it turned, as if the house had been waiting for her.

The door swung open.

Inside, the air smelled of dust and old wood and something else—something sweet and cloying that made her stomach turn. Emma stepped across the threshold, and the door clicked shut behind her with a soft, final sound.

For a moment, she stood in the foyer, letting her eyes adjust to the dim light filtering through the heavy curtains. The real estate agent had promised that the house was fully furnished, and she hadn't been lying. Antique furniture filled every room, each piece covered in a thin layer of dust.

Emma moved through the ground floor, cataloging everything. A formal living room with a fireplace large enough to stand in. A dining room with a table that could seat twelve. A kitchen that looked like it hadn't been updated since 1950, complete with avocado-green appliances.

She was reaching for the back door when she heard it: a sound from upstairs. Soft and rhythmic, like someone slowly pacing back and forth.

Emma froze, her hand on the doorknob.

"Hello?" Her voice echoed in the empty house.

The pacing stopped.

Emma waited, heart hammering, but the silence stretched on. Finally, she exhaled. Old houses made noises. Everyone knew that. It was probably just the wood settling, or a draft moving through the walls, or—

The pacing started again.

This time, Emma could pinpoint the location: directly above her head, in what must be the master bedroom. The same steady rhythm. Back and forth. Back and forth.

She should leave. She should walk out that front door and get in her car and drive back to the city and forget this whole thing. But the house had cost nearly every penny she had left, and she had nowhere else to go.

Instead, Emma found herself climbing the stairs.

The pacing grew louder as she ascended. By the time she reached the second-floor landing, she could hear the distinct sound of footsteps on wood. Heavy footsteps. Not the delicate tread of a ghost or a trick of the imagination, but the solid, unmistakable sound of someone walking.

The master bedroom door stood slightly ajar.

Emma pushed it open.

The room was empty. Dust motes danced in the afternoon light streaming through the windows. An enormous four-poster bed dominated the space, its canopy yellowed with age. But there was no one there. No one who could have been making that sound.

The pacing had stopped again.

Emma stood in the doorway, scanning every corner of the room, her rational mind fighting against the growing certainty that something was very, very wrong with this house.

Then she saw it: a message, written in the dust on the dresser mirror.

LEAVE

Two words. No signature. No explanation.

Emma's hand trembled as she reached out to touch the letters. The dust was fresh—recently disturbed. Which meant someone had been in this room. Recently.

Which meant she wasn't alone.

The sound of the front door slamming made her jump. Then came the unmistakable sound of the deadbolt sliding into place.

Emma ran to the window, but it was painted shut, the wood swollen with decades of moisture. She tried the bedroom door—locked from the outside, though she'd left it open.

And from somewhere deep in the house, she heard laughter.

Not human laughter. Something older. Something that had been waiting a very long time.

Emma Crawford had bought a house in Millbrook.

She was beginning to suspect it would be the last thing she ever did.`,
    lookingFor: [
      'Overall impression and emotional impact',
      'Pacing and tension building',
      'Character development and believability',
      'Plot holes or inconsistencies',
      'Grammar and prose quality',
      'Comparison to similar works in the genre',
    ],
    timeline: {
      readingDeadline: '2024-03-01',
      estimatedWeeks: '4 weeks',
      feedbackFormat: 'Chapter-by-chapter notes plus overall summary',
    },
    writerPreferences: {
      communicationStyle: 'Open to regular check-ins',
      feedbackStyle: 'Honest and detailed',
      experienceLevel: 'Any - beginners welcome',
    },
  };

  const handleApply = async () => {
    if (showApplicationForm) {
      if (!applicationMessage.trim()) {
        window.alert('Please tell the author why you\'re interested in beta reading this manuscript.');
        return;
      }

      console.log('🚀 Submitting beta application...');
      try {
        const response = await fetch(API_ENDPOINTS.betaApplications.submit, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            manuscriptId,
            whyInterested: applicationMessage,
            estimatedTimeline: '',
            betaReadingExperience: null,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          console.error('❌ Application failed:', data.error);
          window.alert(data.error || 'Failed to submit application');
          return;
        }

        console.log('✅ Application submitted successfully!');
        window.alert('Application submitted! The author will review your application and get back to you soon.');
        window.location.href = '/reader/applications';
      } catch (err) {
        console.error('❌ Submit error:', err);
        window.alert('Network error. Please try again.');
      }
    } else {
      setShowApplicationForm(true);
      window.scrollTo(0, 0);
    }
  };

  const handleSave = () => {
    setSaved(!saved);
    window.alert(saved ? 'Removed from saved manuscripts' : 'Saved for later!');
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
      }}>
        <Header />
        <main style={{
          maxWidth: '1000px',
          margin: '0 auto',
          padding: '60px 40px',
        }}>
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading manuscript...
              </p>
            </div>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error || !manuscript) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
      }}>
        <Header />
        <main style={{
          maxWidth: '1000px',
          margin: '0 auto',
          padding: '60px 40px',
        }}>
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.error,
                marginBottom: '20px',
              }}>
                {error || 'Manuscript not found'}
              </p>
              <Button onClick={() => window.location.href = '/manuscripts/discover'}>
                Back to Discover
              </Button>
            </div>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (showApplicationForm) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
      }}>
        <Header />
        <main style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '60px 40px',
        }}>
          <div style={{ marginBottom: '32px' }}>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '8px',
              color: theme.colors.textPrimary,
            }}>
              Apply to Beta Read
            </h1>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              {manuscript.title} by {manuscript.author.name}
            </p>
          </div>

          <Card>
            <Textarea
              label="Why are you interested in beta reading this manuscript?"
              placeholder="Tell the author about your interest in this project, your relevant reading experience, and what you hope to provide as a beta reader..."
              value={applicationMessage}
              onChange={(e) => setApplicationMessage(e.target.value)}
              rows={8}
              required
              helperText="Share your enthusiasm and qualifications"
            />

            <div style={{
              marginTop: '24px',
              padding: '16px',
              backgroundColor: theme.colors.backgroundAlt,
              borderRadius: theme.borderRadius.medium,
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '500',
                marginBottom: '12px',
                color: theme.colors.textPrimary,
              }}>
                Commitment Details
              </h3>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                fontSize: '14px',
                lineHeight: '1.8',
                color: theme.colors.textSecondary,
              }}>
                <li>Reading deadline: {manuscript.timeline.readingDeadline}</li>
                <li>Estimated time: {manuscript.timeline.estimatedWeeks}</li>
                <li>Feedback format: {manuscript.timeline.feedbackFormat}</li>
                <li>Word count: {manuscript.wordCount.toLocaleString()} words</li>
              </ul>
            </div>
          </Card>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px',
            marginTop: '24px',
          }}>
            <Button variant="ghost" onClick={() => setShowApplicationForm(false)}>
              ← Back
            </Button>
            <Button onClick={handleApply}>
              Submit Application
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '40px',
        }}>
          <Button
            variant="ghost"
            onClick={() => window.location.href = '/manuscripts/discover'}
          >
            ← Back to Browse
          </Button>
          <Button
            variant="outline"
            onClick={handleSave}
          >
            {saved ? '✓ Saved' : 'Save for Later'}
          </Button>
        </div>

        {/* Title and Author */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '48px',
            fontWeight: '400',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
            lineHeight: '1.2',
          }}>
            {manuscript.title}
          </h1>
          <p style={{
            fontSize: '24px',
            color: theme.colors.textSecondary,
            marginBottom: '24px',
          }}>
            by {manuscript.author.name}
          </p>

          {/* Metadata */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
          }}>
            <Badge variant="primary">{manuscript.genre}</Badge>
            {manuscript.subGenres.map((subGenre: string) => (
              <Badge key={subGenre} variant="default">{subGenre}</Badge>
            ))}
            <Badge variant="default">{manuscript.wordCount.toLocaleString()} words</Badge>
            <Badge variant="success">{manuscript.completionStatus}</Badge>
          </div>
        </div>

        {/* Content Warnings */}
        {manuscript.contentWarnings.length > 0 && (
          <Card style={{
            backgroundColor: theme.colors.warningLight,
            borderLeft: `4px solid ${theme.colors.warning}`,
            marginBottom: '32px',
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '500',
              marginBottom: '8px',
              color: theme.colors.textPrimary,
            }}>
              Content Warnings
            </h3>
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              margin: 0,
            }}>
              {manuscript.contentWarnings.join(', ')}
            </p>
          </Card>
        )}

        {/* Synopsis */}
        <Card style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Synopsis
          </h2>
          <div style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: theme.colors.textPrimary,
            whiteSpace: 'pre-wrap',
          }}>
            {manuscript.synopsis}
          </div>
        </Card>

        {/* First Pages Sample */}
        <Card style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            First Pages
          </h2>
          <div style={{
            padding: '24px',
            backgroundColor: theme.colors.backgroundAlt,
            borderRadius: theme.borderRadius.medium,
            fontSize: '15px',
            lineHeight: '1.8',
            color: theme.colors.textPrimary,
            whiteSpace: 'pre-wrap',
            fontFamily: theme.fonts.monospace,
            maxHeight: '600px',
            overflow: 'auto',
          }}>
            {manuscript.firstPages}
          </div>
        </Card>

        {/* What the Author is Looking For */}
        <Card style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            What the Author is Looking For
          </h2>
          <ul style={{
            margin: 0,
            paddingLeft: '24px',
            fontSize: '16px',
            lineHeight: '1.8',
            color: theme.colors.textSecondary,
          }}>
            {manuscript.lookingFor.map((item: string, index: number) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </Card>

        {/* Timeline and Expectations */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
          marginBottom: '32px',
        }}>
          <Card>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              marginBottom: '12px',
              color: theme.colors.textPrimary,
            }}>
              Timeline
            </h3>
            <div style={{
              fontSize: '14px',
              lineHeight: '1.8',
              color: theme.colors.textSecondary,
            }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Reading Deadline:</strong> {manuscript.timeline.readingDeadline}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Estimated Time:</strong> {manuscript.timeline.estimatedWeeks}
              </div>
              <div>
                <strong>Feedback Format:</strong> {manuscript.timeline.feedbackFormat}
              </div>
            </div>
          </Card>

          <Card>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              marginBottom: '12px',
              color: theme.colors.textPrimary,
            }}>
              Writer Preferences
            </h3>
            <div style={{
              fontSize: '14px',
              lineHeight: '1.8',
              color: theme.colors.textSecondary,
            }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Communication:</strong> {manuscript.writerPreferences.communicationStyle}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Feedback Style:</strong> {manuscript.writerPreferences.feedbackStyle}
              </div>
              <div>
                <strong>Experience Level:</strong> {manuscript.writerPreferences.experienceLevel}
              </div>
            </div>
          </Card>
        </div>

        {/* About the Author */}
        <Card style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            About the Author
          </h2>
          <p style={{
            fontSize: '15px',
            lineHeight: '1.8',
            color: theme.colors.textSecondary,
            margin: 0,
          }}>
            {manuscript.author.bio}
          </p>
        </Card>

        {/* Application CTA */}
        <Card style={{
          backgroundColor: theme.colors.primaryLight,
          textAlign: 'center',
        }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '400',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Interested in Beta Reading?
          </h2>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.6',
            color: theme.colors.textSecondary,
            marginBottom: '24px',
            maxWidth: '600px',
            margin: '0 auto 24px',
          }}>
            Apply to beta read this manuscript and help the author improve their work.
            The author will review your application and get back to you soon.
          </p>
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            <Button onClick={handleApply}>
              Apply to Beta Read
            </Button>
            <Button variant="outline" onClick={handleSave}>
              {saved ? '✓ Saved' : 'Save for Later'}
            </Button>
          </div>
        </Card>
      </main>

      <Footer />
    </div>
  );
};
